import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Star, RefreshCw, ArrowLeft, Check, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const LOGO = "https://customer-assets.emergentagent.com/job_ff078f22-bc2b-467a-b845-6483b385dad8/artifacts/c2lq75mw_image.png";

const STATUS_META = {
    pending:  { label: "En attente",  className: "bg-kayama-gold text-white" },
    approved: { label: "Publié",      className: "bg-emerald-600 text-white" },
    rejected: { label: "Rejeté",      className: "bg-kayama-brown-3 text-white" },
};

function StatusBadge({ s }) {
    const m = STATUS_META[s] || STATUS_META.pending;
    return <span className={`inline-block text-[10px] uppercase tracking-[0.18em] px-3 py-1 ${m.className}`}>{m.label}</span>;
}

function Stars({ value }) {
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={14} className={n <= value ? "fill-kayama-gold text-kayama-gold" : "text-kayama-line"} />
            ))}
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

export default function AdminReviews() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending");

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/reviews");
            setItems(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    async function setStatus(id, status) {
        try {
            const { data } = await api.patch(`/admin/reviews/${id}/status`, { status });
            setItems((arr) => arr.map((x) => (x.id === id ? data : x)));
            toast.success(status === "approved" ? "Avis publié sur le site." : status === "rejected" ? "Avis rejeté." : "Statut mis à jour.");
        } catch (err) { toast.error(formatApiError(err)); }
    }
    async function remove(id) {
        if (!window.confirm("Supprimer cet avis définitivement ?")) return;
        try {
            await api.delete(`/admin/reviews/${id}`);
            setItems((arr) => arr.filter((x) => x.id !== id));
            toast.success("Avis supprimé.");
        } catch (err) { toast.error(formatApiError(err)); }
    }
    function handleLogout() { logout(); nav("/admin/login"); }

    const filtered = filter === "all" ? items : items.filter((r) => r.status === filter);
    const stats = items.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {});

    return (
        <div className="min-h-screen bg-kayama-cream" data-testid="page-admin-reviews">
            <header className="bg-kayama-paper border-b border-kayama-line sticky top-0 z-30">
                <div className="container-k flex items-center justify-between py-4 flex-wrap gap-3">
                    <div className="flex items-center gap-4">
                        <Link to="/admin" className="text-kayama-brown-3 hover:text-kayama-pink" data-testid="reviews-back"><ArrowLeft size={18} /></Link>
                        <img src={LOGO} alt="Kayama Admin" className="h-10 w-auto" />
                        <span className="font-heading text-2xl text-kayama-brown hidden md:inline">Avis clients</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/" target="_blank" className="link-gold text-[11px]" data-testid="reviews-view-site">Voir le site</Link>
                        <span className="text-xs text-kayama-brown-2 hidden md:inline">{user?.email}</span>
                        <button onClick={handleLogout} className="btn-outline-k" data-testid="reviews-logout"><LogOut size={14} /> Déconnexion</button>
                    </div>
                </div>
            </header>

            <div className="container-k py-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
                    <div>
                        <p className="overline">Modération</p>
                        <h1 className="mt-2 font-heading text-4xl md:text-5xl font-light text-kayama-brown">Avis clients</h1>
                        <p className="mt-2 text-sm text-kayama-brown-2 max-w-xl">Acceptez les avis que vous souhaitez afficher sur le site. Seuls les avis publiés apparaissent publiquement.</p>
                    </div>
                    <button onClick={load} className="btn-ghost-k" data-testid="reviews-refresh"><RefreshCw size={14} /> Rafraîchir</button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {[
                        ["all", "Total", items.length],
                        ["pending", "En attente", stats.pending || 0],
                        ["approved", "Publiés", stats.approved || 0],
                        ["rejected", "Rejetés", stats.rejected || 0],
                    ].map(([k, l, v]) => (
                        <button
                            key={k}
                            onClick={() => setFilter(k)}
                            className={`bg-kayama-paper border p-5 text-left transition ${filter === k ? "border-kayama-pink shadow-[0_4px_20px_rgba(216,27,96,0.12)]" : "border-kayama-line"}`}
                            data-testid={`reviews-filter-${k}`}
                        >
                            <p className="overline">{l}</p>
                            <p className={`mt-2 font-heading text-3xl ${filter === k ? "text-kayama-pink" : "text-kayama-brown"}`}>{v}</p>
                        </button>
                    ))}
                </div>

                {loading ? (
                    <p className="text-center text-kayama-brown-3 py-10">Chargement…</p>
                ) : filtered.length === 0 ? (
                    <div className="bg-kayama-paper border border-kayama-line p-10 text-center" data-testid="reviews-empty">
                        <Star size={28} className="text-kayama-gold mx-auto" />
                        <p className="mt-3 font-heading text-2xl text-kayama-brown">Aucun avis dans cette catégorie.</p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-5">
                        {filtered.map((r) => (
                            <article key={r.id} className="bg-kayama-paper border border-kayama-line p-6 flex flex-col" data-testid={`admin-review-${r.id}`}>
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <div>
                                        <Stars value={r.rating} />
                                        <p className="font-heading text-xl text-kayama-brown mt-2">{r.author_name}</p>
                                        <p className="overline mt-1">{r.event_type ? `${r.event_type} · ` : ""}{formatDate(r.created_at)}</p>
                                    </div>
                                    <StatusBadge s={r.status} />
                                </div>
                                <p className="text-sm text-kayama-brown-2 leading-relaxed italic flex-1">« {r.comment} »</p>
                                <div className="mt-5 pt-4 border-t border-kayama-line flex flex-wrap items-center gap-2">
                                    {r.status !== "approved" && (
                                        <button onClick={() => setStatus(r.id, "approved")} className="inline-flex items-center gap-1 px-3 py-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white text-xs uppercase tracking-[0.15em] transition-colors" data-testid={`approve-${r.id}`}>
                                            <Check size={12} /> Publier
                                        </button>
                                    )}
                                    {r.status !== "rejected" && (
                                        <button onClick={() => setStatus(r.id, "rejected")} className="inline-flex items-center gap-1 px-3 py-2 border border-kayama-line text-kayama-brown-3 hover:bg-kayama-brown-3 hover:text-white text-xs uppercase tracking-[0.15em] transition-colors" data-testid={`reject-${r.id}`}>
                                            <X size={12} /> Rejeter
                                        </button>
                                    )}
                                    {r.status !== "pending" && (
                                        <button onClick={() => setStatus(r.id, "pending")} className="inline-flex items-center gap-1 px-3 py-2 border border-kayama-gold text-kayama-brown hover:bg-kayama-gold hover:text-white text-xs uppercase tracking-[0.15em] transition-colors" data-testid={`pending-${r.id}`}>
                                            Remettre en attente
                                        </button>
                                    )}
                                    <span className="flex-1" />
                                    <button onClick={() => remove(r.id)} className="p-2 text-kayama-brown-3 hover:text-kayama-pink" data-testid={`delete-${r.id}`} aria-label="Supprimer"><Trash2 size={16} /></button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
